import { useEffect } from "react";

/**
 * トップページの動き。ui-mock-g.html の script をそのまま移した。
 *
 * 方針は progressive enhancement。JS が動かなくても内容は読める状態を保つ
 * （opacity:0 で隠さない）。2026-09-10 に .card{opacity:0} + IntersectionObserver で
 * 内容が見えなくなった事故があったため、既定は可視のまま入場だけ付ける。
 *
 * やること3つ:
 *   1. 読了進行線（--progress）
 *   2. 節ごとの進行（--section-progress）と、コンサル本文の現在行
 *   3. 初回可視で下から入る（reduced-motion では無効）
 */
export const useHomeMotion = () => {
  useEffect(() => {
    const reduce = matchMedia("(prefers-reduced-motion: reduce)");
    const sections = [...document.querySelectorAll<HTMLElement>("main>section")];
    let frame = 0;

    const animate = (el: Element, frames: Keyframe[], options: KeyframeAnimationOptions) => {
      if (!reduce.matches && el.animate) return el.animate(frames, options);
      return undefined;
    };

    const update = () => {
      frame = 0;
      const height = innerHeight;
      const max = document.documentElement.scrollHeight - height;
      document.documentElement.style.setProperty(
        "--progress",
        String(max > 0 ? Math.min(1, Math.max(0, scrollY / max)) : 0),
      );
      for (const section of sections) {
        const r = section.getBoundingClientRect();
        section.style.setProperty(
          "--section-progress",
          String(Math.min(1, Math.max(0, (height * 0.75 - r.top) / (r.height + height * 0.25)))),
        );
      }
      const paras = [...document.querySelectorAll<HTMLElement>(".consulting-copy p")];
      let reading = paras[0];
      for (const p of paras) if (p.getBoundingClientRect().top < height * 0.52) reading = p;
      for (const p of paras) p.toggleAttribute("data-current", p === reading);
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    let observer: IntersectionObserver | undefined;
    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            animate(entry.target, [{ translate: "0 22px" }, { translate: "0 0" }], {
              duration: 620,
              easing: "cubic-bezier(.22,1,.36,1)",
            });
            observer?.unobserve(entry.target);
          }
        },
        { threshold: 0.12 },
      );
      document.querySelectorAll("[data-reveal]").forEach((el) => observer?.observe(el));
    }

    const onReduceChange = () => {
      if (reduce.matches) document.getAnimations().forEach((a) => a.finish());
      schedule();
    };

    reduce.addEventListener("change", onReduceChange);
    addEventListener("scroll", schedule, { passive: true });
    addEventListener("resize", schedule);
    addEventListener("pageshow", schedule);

    let ro: ResizeObserver | undefined;
    if ("ResizeObserver" in window) {
      ro = new ResizeObserver(schedule);
      ro.observe(document.body);
    }

    update();

    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer?.disconnect();
      ro?.disconnect();
      reduce.removeEventListener("change", onReduceChange);
      removeEventListener("scroll", schedule);
      removeEventListener("resize", schedule);
      removeEventListener("pageshow", schedule);
    };
  }, []);
};
